/**
 * UTF-16 surrogate safety utilities.
 *
 * Characters outside the Basic Multilingual Plane (emoji, some CJK/symbols) are
 * stored in JS strings as a *pair* of UTF-16 code units: a high surrogate
 * (U+D800–U+DBFF) immediately followed by a low surrogate (U+DC00–U+DFFF).
 *
 * A "lone" surrogate — a high with no following low, or a low with no preceding
 * high — is ill-formed UTF-16. `JSON.stringify` happily serializes it into a bare
 * `\udXXX` escape, but strict JSON parsers (including the Python parser on
 * Anthropic's API servers) reject it with:
 *
 *     The request body is not valid JSON: no low surrogate in string ...
 *
 * which fails the entire LLM request. Lone surrogates reach us two ways:
 *   1. Slicing a string mid-pair (`.substring`/`.slice` cut between high & low).
 *   2. Corrupt source data (Excel/CSV/DB cells with bad encoding).
 *
 * `stripLoneSurrogates` is the safety net (call before anything is JSON-encoded
 * for an LLM); `safeTruncate` is the prevention (cut without splitting a pair).
 */

// Matches a high surrogate NOT followed by a low surrogate, OR a low surrogate
// NOT preceded by a high surrogate. Valid pairs fail both alternatives and are
// left untouched.
const LONE_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/**
 * Replace every unpaired UTF-16 surrogate with the Unicode replacement
 * character (U+FFFD), making the string safe to `JSON.stringify` and send to an
 * LLM API. Valid surrogate pairs (emoji, etc.) are preserved.
 *
 * Non-strings are returned unchanged so this is safe to call defensively.
 */
export function stripLoneSurrogates<T>(value: T): T {
	if (typeof value !== 'string') return value;
	// Fast path: most strings have no surrogates at all.
	if (!/[\uD800-\uDFFF]/.test(value)) return value;
	return value.replace(LONE_SURROGATE_RE, '�') as unknown as T;
}

/**
 * Truncate a string to at most `maxUnits` UTF-16 code units WITHOUT splitting a
 * surrogate pair. If the cut would land between a high and low surrogate, the
 * dangling high surrogate is dropped (so the result is one unit shorter).
 *
 * Returns only the truncated slice — no ellipsis/suffix — so callers keep full
 * control over any "… (N more)" indicator they append.
 */
export function safeTruncate(text: string, maxUnits: number): string {
	if (typeof text !== 'string' || text.length <= maxUnits || maxUnits < 0) return text;
	let end = maxUnits;
	const lastCode = text.charCodeAt(end - 1);
	// Dangling high surrogate at the cut boundary → drop it to avoid a lone half.
	if (lastCode >= 0xd800 && lastCode <= 0xdbff) end -= 1;
	return text.slice(0, end);
}
